/**
 * Sequence generator for pattern-based IDs.
 *
 * Manages sequential counters stored in the database with support for:
 * - Multiple sequences per entity (scoped by table name)
 * - Periodic resets (yearly, monthly, daily)
 * - Atomic increment operations
 */

import type { Knex } from 'knex'

/**
 * Table name for storing sequence counters
 */
export const SEQUENCES_TABLE = '_nexus_sequences'

/**
 * Pattern configuration for ID generation
 */
export interface PatternConfig {
  pattern: string
  prefix?: string
  suffix?: string
  resetOn?: 'never' | 'year' | 'month' | 'day'
  startAt?: number
}

/**
 * Ensures the sequences table exists in the database.
 */
export async function ensureSequencesTable(knex: Knex): Promise<void> {
  const exists = await knex.schema.hasTable(SEQUENCES_TABLE)
  if (!exists) {
    await knex.schema.createTable(SEQUENCES_TABLE, (table) => {
      table.string('id', 100).primary()
      table.string('table_name', 100).notNullable()
      table.string('scope', 20).notNullable() // e.g., '2025', '2025-01', '2025-01-15', or 'global'
      table.integer('current_value').notNullable().defaultTo(0)
      table.timestamp('created_at').defaultTo(knex.fn.now())
      table.timestamp('updated_at').defaultTo(knex.fn.now())

      table.unique(['table_name', 'scope'])
    })
  }
}

/**
 * Get the scope string based on reset configuration.
 */
function getScope(resetOn: PatternConfig['resetOn']): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')

  switch (resetOn) {
    case 'year':
      return String(year)
    case 'month':
      return `${year}-${month}`
    case 'day':
      return `${year}-${month}-${day}`
    case 'never':
    default:
      return 'global'
  }
}

/**
 * Get the next sequence number for a table, atomically incrementing the counter.
 *
 * @param knex - Knex instance
 * @param tableName - Entity table name (used as sequence key)
 * @param config - Pattern configuration
 * @returns Next sequence number
 */
export async function getNextSequence(
  knex: Knex,
  tableName: string,
  config: PatternConfig
): Promise<number> {
  const scope = getScope(config.resetOn)
  const id = `${tableName}:${scope}`
  const startAt = config.startAt ?? 1

  // Use transaction for atomic increment
  return await knex.transaction(async (trx) => {
    // Try to get existing sequence
    const existing = await trx(SEQUENCES_TABLE)
      .where('id', id)
      .first()

    if (existing) {
      // Increment and return
      const nextValue = existing.current_value + 1
      await trx(SEQUENCES_TABLE)
        .where('id', id)
        .update({
          current_value: nextValue,
          updated_at: trx.fn.now()
        })
      return nextValue
    } else {
      // Create new sequence
      await trx(SEQUENCES_TABLE).insert({
        id,
        table_name: tableName,
        scope,
        current_value: startAt,
        created_at: trx.fn.now(),
        updated_at: trx.fn.now()
      })
      return startAt
    }
  })
}

/**
 * Generate an ID based on a pattern template.
 *
 * Supported tokens:
 * - {prefix} - Static prefix string
 * - {suffix} - Static suffix string
 * - {year} - Current year (4 digits)
 * - {year2} - Current year (2 digits)
 * - {month} - Current month (01-12)
 * - {day} - Current day (01-31)
 * - {seq} - Sequence number
 * - {seq:N} - Sequence with N-digit zero-padding
 *
 * @param knex - Knex instance
 * @param tableName - Entity table name
 * @param config - Pattern configuration
 * @returns Generated ID string
 *
 * @example
 * ```typescript
 * // Pattern: '{prefix}-{year}-{seq:6}' with prefix: 'TP'
 * // Result: 'TP-2025-000001'
 *
 * // Pattern: '{prefix}{year2}{month}-{seq:4}' with prefix: 'INV'
 * // Result: 'INV2501-0001'
 * ```
 */
export async function generatePatternId(
  knex: Knex,
  tableName: string,
  config: PatternConfig
): Promise<string> {
  const now = new Date()
  const year = String(now.getFullYear())
  const year2 = year.slice(-2)
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')

  // Get next sequence number
  const seq = await getNextSequence(knex, tableName, config)

  // Replace tokens in pattern
  let result = config.pattern

  // Replace simple tokens
  result = result.replace(/\{prefix\}/g, config.prefix ?? '')
  result = result.replace(/\{suffix\}/g, config.suffix ?? '')
  result = result.replace(/\{year\}/g, year)
  result = result.replace(/\{year2\}/g, year2)
  result = result.replace(/\{month\}/g, month)
  result = result.replace(/\{day\}/g, day)

  // Replace sequence tokens with padding
  result = result.replace(/\{seq:(\d+)\}/g, (_match, padding) => {
    const padLength = parseInt(padding, 10)
    return String(seq).padStart(padLength, '0')
  })

  // Replace simple sequence token
  result = result.replace(/\{seq\}/g, String(seq))

  return result
}

